import React, { useState, useEffect } from 'react';
import { User, WorkOrder, Environment } from '../types';
import { db } from '../services/db';
import { PageHeader } from './ui/PageHeader';
import { Button } from './ui/Button';
import { 
  ShoppingCart, 
  Plus, 
  Trash2, 
  Pencil, 
  Check, 
  X, 
  Download, 
  Search, 
  Filter, 
  DollarSign, 
  Calendar, 
  User as UserIcon, 
  FileText,
  AlertCircle,
  PlusCircle,
  List,
  TrendingUp
} from 'lucide-react';
import { UpgradeRequired } from './ui/UpgradeRequired';
import { ConfirmModal } from './ui/ConfirmModal';

interface ExpensesManagerProps {
  currentUser: User;
  orders: WorkOrder[];
  currentCompany: any;
  onChangeView: (view: string) => void;
}

export const ExpensesManager: React.FC<ExpensesManagerProps> = ({ currentUser, orders, currentCompany, onChangeView }) => {
  const isPro = ['pro', 'business'].includes(currentCompany?.plano?.toLowerCase());

  if (!isPro) {
    return (
      <UpgradeRequired
        featureName="Gastos com Materiais e Ferramentas"
        requiredPlan="Pro"
        onUpgrade={() => onChangeView('UPGRADE')}
      />
    );
  }
  const [confirmModal, setConfirmModal] = useState<{
    title: string;
    message: string;
    confirmLabel?: string;
    cancelLabel?: string;
    confirmStyle?: 'danger' | 'primary';
    onConfirm: () => void;
    onCancel?: () => void;
  } | null>(null);

  const [activeTab, setActiveTab] = useState<'BY_OS' | 'GENERAL'>('BY_OS');
  
  // State for "Por O.S." tab
  const [osSearch, setOsSearch] = useState('');
  const [selectedOrderId, setSelectedOrderId] = useState('');
  const [orderExpenses, setOrderExpenses] = useState<any[]>([]);
  const [newExpense, setNewExpense] = useState({
    item_name: '',
    environment_id: '',
    quantity: 1,
    unit_value_str: '0,00',
  });
  
  // State for "Painel Geral" tab
  const [allExpenses, setAllExpenses] = useState<any[]>([]);
  const [filterPeriod, setFilterPeriod] = useState('30'); // '30', '90', '180', '365', 'ALL'
  const [filterTechnician, setFilterTechnician] = useState('ALL');
  const [filterStatus, setFilterStatus] = useState('ALL');
  const [filterItemName, setFilterItemName] = useState('');

  // Editing state
  const [editingExpenseId, setEditingExpenseId] = useState<string | null>(null);
  const [editingExpenseData, setEditingExpenseData] = useState<any>(null);

  const [loading, setLoading] = useState(false);

  // Load expenses for selected order
  const loadOrderExpenses = async (orderId: string) => {
    if (!orderId) {
      setOrderExpenses([]);
      return;
    }
    setLoading(true);
    try {
      const data = await db.getExpensesByOrder(orderId);
      setOrderExpenses(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  // Load all expenses for general panel
  const loadAllExpenses = async () => {
    setLoading(true);
    try {
      const data = await db.getAllExpenses();
      setAllExpenses(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'BY_OS' && selectedOrderId) {
      loadOrderExpenses(selectedOrderId);
    } else if (activeTab === 'GENERAL') {
      loadAllExpenses();
    }
  }, [activeTab, selectedOrderId]);

  // Helpers for BRL currency parsing and formatting
  const formatBRL = (val: number) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val);
  };

  const formatNumberBRL = (val: number) => {
    return new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(val);
  };

  const formatOrderId = (id: string) => {
    if (!id) return '';
    const upper = id.toUpperCase();
    if (upper.startsWith('OS-')) {
      return upper;
    }
    return `OS-${upper.substring(0, 8)}`;
  };

  const parseBRL = (valStr: string): number => {
    const cleanStr = valStr.replace(/\D/g, '');
    const valNum = parseFloat(cleanStr) / 100;
    return isNaN(valNum) ? 0 : valNum;
  };

  const handleCurrencyInputChange = (valStr: string, setter: (val: string) => void) => {
    const parsed = parseBRL(valStr);
    setter(formatNumberBRL(parsed));
  };

  // Get selected order details
  const selectedOrder = orders.find(o => o.id === selectedOrderId);
  
  // Calculate selected O.S. metrics
  const orderValue = selectedOrder?.environments?.reduce((sum, env) => sum + (env.value_brl || 0), 0) || 0;
  const expenses = orderExpenses ?? [];
  const orderExpensesTotal = expenses.reduce((sum, exp) => sum + (Number(exp?.total_value) || 0), 0);
  const orderNetResult = orderValue - orderExpensesTotal;

  // Filtered assembly orders for select dropdown
  const filteredOrders = orders
    .filter(o => o.type === 'ASSEMBLY')
    .filter(o => {
      const query = osSearch.toLowerCase();
      return (
        o.clientName.toLowerCase().includes(query) ||
        o.id.toLowerCase().includes(query)
      );
    });

  // Automatically select the first order in the filtered list if none is selected
  useEffect(() => {
    if (filteredOrders.length > 0 && !selectedOrderId) {
      setSelectedOrderId(filteredOrders[0].id);
    }
  }, [osSearch]);

  const handleAddExpense = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedOrderId || !newExpense.item_name.trim()) return;

    const unit_value = parseBRL(newExpense.unit_value_str);
    const success = await db.addExpense({
      order_id: selectedOrderId,
      environment_id: newExpense.environment_id || undefined,
      item_name: newExpense.item_name,
      quantity: newExpense.quantity,
      unit_value,
      registered_by: currentUser.name,
    });

    if (success) {
      setNewExpense({
        item_name: '',
        environment_id: '',
        quantity: 1,
        unit_value_str: '0,00',
      });
      loadOrderExpenses(selectedOrderId);
    } else {
      setConfirmModal({
        title: 'Erro',
        message: 'Erro ao registrar gasto. Verifique suas permissões.',
        confirmLabel: 'OK',
        onConfirm: () => setConfirmModal(null)
      });
    }
  };

  const handleStartEdit = (exp: any) => {
    setEditingExpenseId(exp.id);
    setEditingExpenseData({
      item_name: exp.item_name,
      environment_id: exp.environment_id || '',
      quantity: exp.quantity,
      unit_value_str: formatNumberBRL(Number(exp.unit_value)),
    });
  };

  const handleSaveEdit = async (id: string) => {
    if (!editingExpenseData || !editingExpenseData.item_name.trim()) return;

    try {
      await db.updateExpense(id, {
        item_name: editingExpenseData.item_name,
        environment_id: editingExpenseData.environment_id || null,
        quantity: Number(editingExpenseData.quantity),
        unit_value: parseBRL(editingExpenseData.unit_value_str),
      });
      setEditingExpenseId(null);
      setEditingExpenseData(null);
      loadOrderExpenses(selectedOrderId);
    } catch (e) {
      setConfirmModal({
        title: 'Erro',
        message: 'Erro ao atualizar gasto.',
        confirmLabel: 'OK',
        onConfirm: () => setConfirmModal(null)
      });
    }
  };

  const handleDeleteExpense = (id: string) => {
    setConfirmModal({
      title: 'Excluir gasto',
      message: 'Tem certeza de que deseja excluir este gasto?',
      confirmLabel: 'Excluir',
      cancelLabel: 'Cancelar',
      confirmStyle: 'danger',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          await db.deleteExpense(id);
          loadOrderExpenses(selectedOrderId);
        } catch (e) {
          setConfirmModal({
            title: 'Erro',
            message: 'Erro ao excluir gasto.',
            confirmLabel: 'OK',
            onConfirm: () => setConfirmModal(null)
          });
        }
      },
      onCancel: () => setConfirmModal(null)
    });
  };

  // Helper to extract order data safely whether it is returned as an object or an array from Supabase join
  const getOrderData = (exp: any) => {
    if (!exp) return null;
    if (exp.orders) {
      return Array.isArray(exp.orders) ? exp.orders[0] : exp.orders;
    }
    return null;
  };

  // Helper to format name to "First Name + Last Name Initial" (e.g. "João L.") or full name
  const formatTechnicianName = (name: string) => {
    if (!name) return 'Sem técnico';
    const cleanName = name.trim();
    if (!cleanName) return 'Sem técnico';
    const parts = cleanName.split(/\s+/);
    if (parts.length > 1) {
      const firstName = parts[0];
      const initial = parts[1][0].toUpperCase();
      return `${firstName} ${initial}.`;
    }
    return cleanName;
  };

  // Helper to extract the resolved technician ID for an expense
  const getExpenseTechnicianId = (exp: any) => {
    if (!exp) return null;
    const dbOrder = getOrderData(exp);
    const order = (orders || []).find(o => o.id === exp.order_id);
    
    let techId = dbOrder?.technicianId || order?.technicianId;
    if (!techId) {
      if (dbOrder?.environments && dbOrder.environments.length > 0) {
        const env = dbOrder.environments.find((e: any) => e.technicianId);
        if (env) techId = env.technicianId;
      }
    }
    if (!techId) {
      if (order?.environments && order.environments.length > 0) {
        const env = order.environments.find(e => e.technicianId);
        if (env) techId = env.technicianId;
      }
    }
    return techId;
  };

  // Helper to extract the resolved technician Name for an expense
  const getExpenseTechnicianName = (exp: any) => {
    if (!exp) return 'Sem técnico';
    const dbOrder = getOrderData(exp);
    const order = (orders || []).find(o => o.id === exp.order_id);
    
    let techName = dbOrder?.technicianName || order?.technicianName;
    if (!techName) {
      if (dbOrder?.environments && dbOrder.environments.length > 0) {
        const env = dbOrder.environments.find((e: any) => e.technicianName);
        if (env) techName = env.technicianName;
      }
    }
    if (!techName) {
      if (order?.environments && order.environments.length > 0) {
        const env = order.environments.find(e => e.technicianName);
        if (env) techName = env.technicianName;
      }
    }
    return formatTechnicianName(techName);
  };

  // Filter general expenses
  const filteredGeneralExpenses = (allExpenses || []).filter(exp => {
    if (!exp) return false;
    // 1. Period Filter
    if (filterPeriod !== 'ALL') {
      const days = parseInt(filterPeriod);
      if (!isNaN(days) && exp.registered_at) {
        const limitDate = new Date();
        limitDate.setDate(limitDate.getDate() - days);
        if (new Date(exp.registered_at) < limitDate) return false;
      }
    }
    // 2. Technician Filter
    if (filterTechnician !== 'ALL') {
      const techId = getExpenseTechnicianId(exp);
      if (techId !== filterTechnician) return false;
    }
    // 3. Status Filter
    if (filterStatus !== 'ALL') {
      const order = getOrderData(exp);
      const osStatus = order?.status;
      if (filterStatus === 'IN_PROGRESS') {
        if (osStatus === 'COMPLETED' || osStatus === 'CANCELLED') return false;
      } else if (filterStatus === 'COMPLETED') {
        if (osStatus !== 'COMPLETED') return false;
      }
    }
    // 4. Search Filter
    if (filterItemName && filterItemName.trim()) {
      if (!(exp.item_name || '').toLowerCase().includes(filterItemName.toLowerCase())) return false;
    }
    return true;
  });

  // Calculate General Panel Metrics
  const generalExpensesTotal = (filteredGeneralExpenses || []).reduce((sum, exp) => sum + (Number(exp?.total_value) || 0), 0);
  
  const uniqueOsIds = Array.from(new Set((filteredGeneralExpenses || []).map(exp => exp?.order_id).filter(Boolean)));
  const numOsWithExpenses = uniqueOsIds.length;
  const averageExpensePerOs = numOsWithExpenses > 0 ? generalExpensesTotal / numOsWithExpenses : 0;

  // Item mais comprado
  const itemCounts: Record<string, number> = {};
  (filteredGeneralExpenses || []).forEach(exp => {
    if (exp) {
      const name = (exp.item_name || '').trim();
      if (name) {
        itemCounts[name] = (itemCounts[name] || 0) + Number(exp.quantity || 0);
      }
    }
  });
  let mostPurchasedItem = '-';
  let mostPurchasedQty = 0;
  Object.entries(itemCounts).forEach(([name, qty]) => {
    if (qty > mostPurchasedQty) {
      mostPurchasedQty = qty;
      mostPurchasedItem = name;
    }
  });

  // Consolidate expenses by O.S. for General Dashboard table
  const osConsolidatedMap = new Map<string, {
    orderId: string;
    clientName: string;
    technicianName: string;
    status: string;
    orderValue: number;
    expensesTotal: number;
  }>();

  (filteredGeneralExpenses || []).forEach(exp => {
    if (!exp) return;
    const oId = exp.order_id;
    if (!oId) return;
    if (!osConsolidatedMap.has(oId)) {
      const order = (orders || []).find(o => o.id === oId);
      const val = order?.environments?.reduce((sum, env) => sum + (Number(env?.value_brl) || 0), 0) || 0;
      const dbOrder = getOrderData(exp);
      osConsolidatedMap.set(oId, {
        orderId: oId,
        clientName: dbOrder?.clientName || order?.clientName || 'Desconhecido',
        technicianName: getExpenseTechnicianName(exp),
        status: dbOrder?.status || order?.status || 'OPEN',
        orderValue: val,
        expensesTotal: 0
      });
    }
    const currentObj = osConsolidatedMap.get(oId);
    if (currentObj) {
      currentObj.expensesTotal += Number(exp.total_value) || 0;
    }
  });

  const osConsolidatedList = Array.from(osConsolidatedMap.values());

  const generalBrutoTotal = (osConsolidatedList || []).reduce((sum, item) => sum + (Number(item?.orderValue) || 0), 0);
  const generalGastosTotal = (osConsolidatedList || []).reduce((sum, item) => sum + (Number(item?.expensesTotal) || 0), 0);
  const generalLiquidoTotal = generalBrutoTotal - generalGastosTotal;

  // Consolidate items for ranking table
  const itemRankingMap = new Map<string, {
    name: string;
    occurrences: number;
    totalSpent: number;
  }>();

  (filteredGeneralExpenses || []).forEach(exp => {
    if (!exp) return;
    const name = (exp.item_name || '').trim();
    if (!name) return;
    if (!itemRankingMap.has(name)) {
      itemRankingMap.set(name, {
        name,
        occurrences: 0,
        totalSpent: 0
      });
    }
    const currentObj = itemRankingMap.get(name);
    if (currentObj) {
      currentObj.occurrences += Number(exp.quantity || 0);
      currentObj.totalSpent += Number(exp.total_value) || 0;
    }
  });

  const itemRankingList = Array.from(itemRankingMap.values())
    .sort((a, b) => (b.totalSpent || 0) - (a.totalSpent || 0));

  // CSV Export functions
  const exportCSVOrderExpenses = () => {
    const headers = ['Item', 'Ambiente', 'Quantidade', 'Valor Unitário', 'Total', 'Registrado em', 'Registrado por'];
    const expensesList = expenses ?? [];
    const rows = expensesList.map(exp => {
      if (!exp) return [];
      const envName = selectedOrder?.environments?.find(e => e.id === exp.environment_id)?.name || 'Geral (O.S.)';
      return [
        exp.item_name || '',
        envName,
        exp.quantity || 0,
        Number(exp.unit_value || 0).toFixed(2),
        Number(exp.total_value || 0).toFixed(2),
        exp.registered_at ? new Date(exp.registered_at).toLocaleDateString('pt-BR') : '',
        exp.registered_by || ''
      ];
    });

    const csvContent = [headers.join(';'), ...rows.map(r => r.join(';'))].join('\n');
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `gastos_os_${selectedOrderId}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportCSVGeneralConsolidated = () => {
    if (osConsolidatedList.length === 0) return;

    const headers = ['Nº O.S.', 'Cliente', 'Técnico', 'Status', 'Valor O.S.', 'Gastos', 'Resultado Líquido'];
    const rows = osConsolidatedList.map(item => {
      const net = item.orderValue - item.expensesTotal;
      return [
        item.orderId,
        item.clientName,
        item.technicianName,
        item.status,
        item.orderValue.toFixed(2),
        item.expensesTotal.toFixed(2),
        net.toFixed(2)
      ];
    });

    const csvContent = [headers.join(';'), ...rows.map(r => r.join(';'))].join('\n');
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `painel_geral_gastos.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    // Status mapping for visual badge helper
  };
  
  const getStatusStyle = (status: string) => {
    switch (status) {
      case 'COMPLETED': return { bg: 'rgba(16,185,129,.15)', color: '#34d399', label: 'Concluída' };
      case 'CANCELLED': return { bg: 'rgba(239,68,68,.15)', color: '#f87171', label: 'Cancelada' };
      case 'IN_PROGRESS': return { bg: 'rgba(26,107,255,.15)', color: '#60a5fa', label: 'Em andamento' };
      case 'PENDING': return { bg: 'rgba(245,158,11,.15)', color: '#fbbf24', label: 'Pendente' };
      default: return { bg: 'rgba(245,158,11,.15)', color: '#fbbf24', label: 'Aberta' };
    }
  };

  const getPeriodLabel = () => {
    switch (filterPeriod) {
      case '30': return 'Últimos 30 dias';
      case '90': return 'Últimos 90 dias';
      case '180': return 'Últimos 180 dias';
      case '365': return 'Últimos 12 meses';
      case 'ALL': return 'Todo o período';
      default: return 'Período';
    }
  };

  const getTableStatusStyle = (status: string) => {
    return getStatusStyle(status);
  };

  const getOsBadgeStyle = (status: string) => {
    switch (status) {
      case 'COMPLETED': return { bg: 'rgba(16,185,129,.1)', color: '#34d399' };
      case 'CANCELLED': return { bg: 'rgba(239,68,68,.1)', color: '#f87171' };
      case 'IN_PROGRESS': return { bg: 'rgba(26,107,255,.1)', color: '#60a5fa' };
      default: return { bg: 'rgba(245,158,11,.1)', color: '#fbbf24' };
    }
  };

  // Unique list of technicians from orders and environments for filters
  const techniciansList = Array.from(
    new Set([
      ...orders.map(o => o.technicianId),
      ...orders.flatMap(o => (o.environments || []).map(e => e.technicianId))
    ].filter(Boolean))
  ).map(id => {
    let name = 'Sem nome';
    const orderWithTech = orders.find(o => o.technicianId === id);
    if (orderWithTech?.technicianName) {
      name = orderWithTech.technicianName;
    } else {
      const envWithTech = orders
        .flatMap(o => o.environments || [])
        .find(e => e.technicianId === id);
      if (envWithTech?.technicianName) {
        name = envWithTech.technicianName;
      }
    }
    return { id: id as string, name: formatTechnicianName(name) };
  });

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader 
        title="Gastos com Materiais e Ferramentas"
        description="Gerenciamento de despesas adicionais vinculadas a ambientes e ordens de serviço."
        icon={<ShoppingCart size={22} style={{ color: 'var(--cyan)' }} />}
      />

      {/* Tabs Menu */}
      <div className="flex bg-navy2 p-1.5 rounded-2xl border border-white/5 w-fit gap-2">
        <button
          onClick={() => setActiveTab('BY_OS')}
          className={`px-6 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 ${
            activeTab === 'BY_OS'
              ? 'bg-blue text-white shadow-lg shadow-blue/20'
              : 'text-slate-400 hover:text-white hover:bg-white/5'
          }`}
        >
          Por O.S.
        </button>
        <button
          onClick={() => setActiveTab('GENERAL')}
          className={`px-6 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 ${
            activeTab === 'GENERAL'
              ? 'bg-blue text-white shadow-lg shadow-blue/20'
              : 'text-slate-400 hover:text-white hover:bg-white/5'
          }`}
        >
          Painel Geral
        </button>
      </div>

      {activeTab === 'BY_OS' ? (
        <div className="space-y-6 w-full">
          {/* OS Selector */}
          <div className="card p-6 rounded-3xl flex flex-col gap-4">
            <label className="text-[11px] font-bold uppercase tracking-wider" style={{ color: 'var(--muted)' }}>
              SELECIONAR O.S. DE MONTAGEM
            </label>
            
            <div className="flex flex-col gap-4">
              {/* Search input with label on the right */}
              <div className="flex items-center gap-3">
                <div className="relative flex-1">
                  <Search className="absolute left-3.5 top-3.5 text-slate-400" size={16} />
                  <input
                    type="text"
                    placeholder="Filtrar por nome do cliente ou nº da O.S..."
                    className="input-field pl-10 w-full h-11"
                    value={osSearch}
                    onChange={(e) => setOsSearch(e.target.value)}
                  />
                </div>
                <span className="text-[11px]" style={{ color: 'var(--muted)' }}>
                  Filtra a lista abaixo
                </span>
              </div>

              {/* Select dropdown */}
              <select
                className="input-field w-full h-11"
                value={selectedOrderId}
                onChange={(e) => setSelectedOrderId(e.target.value)}
              >
                {filteredOrders.length === 0 ? (
                  <option value="">Nenhuma O.S. encontrada</option>
                ) : (
                  filteredOrders.map(o => (
                    <option key={o.id} value={o.id}>
                      {formatOrderId(o.id)} — {o.clientName} ({getStatusStyle(o.status).label})
                    </option>
                  ))
                )}
              </select>
            </div>
          </div>

          {selectedOrderId ? (
            <>
              {/* 3 Metrics Cards */}
              <div className="grid grid-cols-3 gap-4 expenses-os-metrics-grid">
                {/* Card 1 */}
                <div className="card p-5 rounded-3xl flex flex-col gap-1">
                  <span className="text-[11px] font-bold uppercase tracking-wider" style={{ color: 'var(--muted)' }}>VALOR DA O.S.</span>
                  <span className="text-2xl font-bold text-white mt-1">{formatBRL(orderValue)}</span>
                  <span className="text-[11px]" style={{ color: 'var(--muted)' }}>Valor contratado</span>
                </div>
                
                {/* Card 2 */}
                <div className="card p-5 rounded-3xl flex flex-col gap-1">
                  <span className="text-[11px] font-bold uppercase tracking-wider" style={{ color: 'var(--muted)' }}>TOTAL EM GASTOS</span>
                  <span className="text-2xl font-bold mt-1" style={{ color: 'var(--sf-red)' }}>− {formatBRL(orderExpensesTotal)}</span>
                  <span className="text-[11px]" style={{ color: 'var(--muted)' }}>
                    {orderExpenses.length} {orderExpenses.length === 1 ? 'item registrado' : 'itens registrados'}
                  </span>
                </div>

                {/* Card 3 */}
                <div className="card p-5 rounded-3xl flex flex-col gap-1">
                  <span className="text-[11px] font-bold uppercase tracking-wider" style={{ color: 'var(--muted)' }}>RESULTADO LÍQUIDO</span>
                  <span className="text-2xl font-bold mt-1" style={{ color: 'var(--sf-green)' }}>{formatBRL(orderNetResult)}</span>
                  <span className="text-[11px]" style={{ color: 'var(--muted)' }}>Após dedução de gastos</span>
                </div>
              </div>

              {/* Card de formulário "Registrar novo gasto" */}
              <form onSubmit={handleAddExpense} className="card p-6 rounded-3xl flex flex-col gap-4">
                <div className="flex items-center gap-2 border-b border-white/5 pb-3 mb-1">
                  <PlusCircle size={18} style={{ color: 'var(--sf-blue)' }} />
                  <h3 className="font-bold text-[16px] text-slate-200">Registrar novo gasto</h3>
                </div>
                
                <div style={{ display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                  {/* 1. Nome do Item */}
                  <div style={{ flex: '2 1 0%', minWidth: 200 }} className="space-y-1.5">
                    <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Nome do item</label>
                    <input
                      type="text"
                      required
                      placeholder="Ex: Cola para fórmica"
                      className="input-field w-full h-11"
                      value={newExpense.item_name}
                      onChange={(e) => setNewExpense({ ...newExpense, item_name: e.target.value })}
                    />
                  </div>

                  {/* 2. Ambiente */}
                  <div style={{ width: 160, flexShrink: 0 }} className="space-y-1.5">
                    <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Ambiente</label>
                    <select
                      className="input-field w-full h-11"
                      value={newExpense.environment_id}
                      onChange={(e) => setNewExpense({ ...newExpense, environment_id: e.target.value })}
                    >
                      <option value="">Geral (O.S.)</option>
                      {selectedOrder?.environments?.map(env => (
                        <option key={env.id} value={env.id}>{env.name}</option>
                      ))}
                    </select>
                  </div>

                  {/* 3. Quantidade */}
                  <div style={{ width: 90, flexShrink: 0 }} className="space-y-1.5">
                    <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Qtd</label>
                    <input
                      type="number"
                      required
                      min={1}
                      className="input-field w-full h-11 text-center"
                      value={newExpense.quantity}
                      onChange={(e) => setNewExpense({ ...newExpense, quantity: Math.max(1, parseInt(e.target.value) || 1) })}
                    />
                  </div>

                  {/* 4. Valor Unitário */}
                  <div style={{ width: 140, flexShrink: 0 }} className="space-y-1.5">
                    <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Valor unit. (R$)</label>
                    <div className="relative">
                      <span className="absolute left-3 top-3 text-[13px] font-bold text-slate-400">R$</span>
                      <input
                        type="text"
                        required
                        className="input-field pl-9 pr-2 w-full h-11 text-right font-semibold"
                        value={newExpense.unit_value_str}
                        onChange={(e) => handleCurrencyInputChange(e.target.value, (val) => setNewExpense({ ...newExpense, unit_value_str: val }))}
                      />
                    </div>
                  </div>

                  {/* 5. Botão Adicionar */}
                  <button
                    type="submit"
                    className="h-11 px-6 rounded-xl font-semibold text-white flex items-center justify-center gap-2 transition-all duration-200"
                    style={{ backgroundColor: 'var(--sf-blue)', flex: '0 0 auto' }}
                  >
                    <Plus size={18} />
                    + Adicionar
                  </button>
                </div>

                <div className="text-[12px]" style={{ color: 'var(--muted)' }}>
                  Total calculado: {formatBRL(newExpense.quantity * parseBRL(newExpense.unit_value_str))}
                </div>
              </form>

              {/* Card de tabela "Itens registrados" */}
              <div className="card rounded-3xl overflow-hidden flex flex-col">
                <div className="p-6 border-b border-white/5 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                  <div className="flex items-center gap-2">
                    <List size={18} style={{ color: 'var(--sf-blue)' }} />
                    <h3 className="font-bold text-[16px] text-slate-200">Itens registrados</h3>
                  </div>
                  {expenses.length > 0 && (
                    <Button
                      variant="secondary"
                      onClick={exportCSVOrderExpenses}
                      icon={<Download size={18} />}
                    >
                      Exportar
                    </Button>
                  )}
                </div>

                {loading ? (
                  <div className="p-12 text-center text-slate-400">Carregando despesas...</div>
                ) : expenses.length === 0 ? (
                  <div className="p-12 text-center text-slate-400 flex flex-col items-center gap-3">
                    <AlertCircle className="text-slate-500" size={32} />
                    <span>Nenhum gasto registrado nesta O.S.</span>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                      <thead>
                        <tr className="bg-navy4/30 border-b border-white/5">
                          <th className="p-4 text-[11px] font-bold uppercase tracking-wider text-slate-400" style={{ minWidth: '30%' }}>ITEM</th>
                          <th className="p-4 text-[11px] font-bold uppercase tracking-wider text-slate-400" style={{ width: 130 }}>AMBIENTE</th>
                          <th className="p-4 text-[11px] font-bold uppercase tracking-wider text-slate-400 text-right" style={{ width: 70, whiteSpace: 'nowrap' }}>QTD</th>
                          <th className="p-4 text-[11px] font-bold uppercase tracking-wider text-slate-400 text-right" style={{ width: 110, whiteSpace: 'nowrap' }}>VALOR UNIT.</th>
                          <th className="p-4 text-[11px] font-bold uppercase tracking-wider text-slate-400 text-right" style={{ width: 110, whiteSpace: 'nowrap' }}>TOTAL</th>
                          <th className="p-4 text-[11px] font-bold uppercase tracking-wider text-slate-400 text-right" style={{ width: 100, whiteSpace: 'nowrap' }}>DATA</th>
                          <th className="p-4 text-[11px] font-bold uppercase tracking-wider text-slate-400 text-right" style={{ width: 80 }}>AÇÕES</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-white/5">
                        {expenses.map((exp) => {
                          const isEditing = editingExpenseId === exp.id;
                          const envName = selectedOrder?.environments?.find(e => e.id === exp.environment_id)?.name || 'Geral (O.S.)';
                          
                          if (isEditing) {
                            return (
                              <tr key={exp.id} className="bg-navy4/10">
                                <td className="p-3" style={{ minWidth: '30%' }}>
                                  <input
                                    type="text"
                                    className="input-field h-9 px-2 text-sm w-full"
                                    value={editingExpenseData.item_name}
                                    onChange={(e) => setEditingExpenseData({ ...editingExpenseData, item_name: e.target.value })}
                                  />
                                </td>
                                <td className="p-3" style={{ width: 130 }}>
                                  <select
                                    className="input-field h-9 px-2 text-sm w-full"
                                    value={editingExpenseData.environment_id}
                                    onChange={(e) => setEditingExpenseData({ ...editingExpenseData, environment_id: e.target.value })}
                                  >
                                    <option value="">Geral (O.S.)</option>
                                    {selectedOrder?.environments?.map(env => (
                                      <option key={env.id} value={env.id}>{env.name}</option>
                                    ))}
                                  </select>
                                </td>
                                <td className="p-3 text-right" style={{ width: 70, whiteSpace: 'nowrap' }}>
                                  <input
                                    type="number"
                                    min={1}
                                    className="input-field h-9 px-2 text-sm text-center w-full"
                                    value={editingExpenseData.quantity}
                                    onChange={(e) => setEditingExpenseData({ ...editingExpenseData, quantity: Math.max(1, parseInt(e.target.value) || 1) })}
                                  />
                                </td>
                                <td className="p-3 text-right" style={{ width: 110, whiteSpace: 'nowrap' }}>
                                  <input
                                    type="text"
                                    className="input-field h-9 px-2 text-sm text-right w-full font-semibold"
                                    value={editingExpenseData.unit_value_str}
                                    onChange={(e) => handleCurrencyInputChange(e.target.value, (val) => setEditingExpenseData({ ...editingExpenseData, unit_value_str: val }))}
                                  />
                                </td>
                                <td className="p-3 text-right text-cyan font-bold" style={{ width: 110, whiteSpace: 'nowrap' }}>
                                  {formatBRL(editingExpenseData.quantity * parseBRL(editingExpenseData.unit_value_str))}
                                </td>
                                <td className="p-3 text-right text-xs text-slate-400" style={{ width: 100, whiteSpace: 'nowrap' }}>
                                  {new Date(exp.registered_at).toLocaleDateString('pt-BR')}
                                </td>
                                <td className="p-3 text-right" style={{ width: 80 }}>
                                  <div className="flex gap-2 justify-end">
                                    <button 
                                      onClick={() => handleSaveEdit(exp.id)}
                                      className="p-1.5 bg-green-500/10 text-green-500 rounded-lg hover:bg-green-500/20"
                                      title="Salvar"
                                    >
                                      <Check size={14} />
                                    </button>
                                    <button 
                                      onClick={() => { setEditingExpenseId(null); setEditingExpenseData(null); }}
                                      className="p-1.5 bg-red-500/10 text-red-500 rounded-lg hover:bg-red-500/20"
                                      title="Cancelar"
                                    >
                                      <X size={14} />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          }

                          return (
                            <tr key={exp.id} className="hover:bg-white/[0.01]">
                              <td className="p-4 font-semibold text-slate-200" style={{ minWidth: '30%' }}>{exp.item_name}</td>
                              <td className="p-4 text-slate-400 text-sm" style={{ width: 130 }}>
                                <span 
                                  className="px-2.5 py-1 bg-white/5 border border-white/5 rounded-full text-xs font-medium text-slate-300 inline-block"
                                  style={{ 
                                    whiteSpace: 'nowrap', 
                                    overflow: 'hidden', 
                                    textOverflow: 'ellipsis', 
                                    maxWidth: '140px',
                                    verticalAlign: 'middle'
                                  }}
                                >
                                  {envName}
                                </span>
                              </td>
                              <td className="p-4 text-right font-semibold" style={{ width: 70, whiteSpace: 'nowrap' }}>{exp.quantity}</td>
                              <td className="p-4 text-right text-slate-300" style={{ width: 110, whiteSpace: 'nowrap' }}>{formatBRL(Number(exp.unit_value))}</td>
                              <td className="p-4 text-right font-bold text-red-400" style={{ width: 110, whiteSpace: 'nowrap' }}>− {formatBRL(Number(exp.total_value))}</td>
                              <td className="p-4 text-right text-slate-400 text-xs" style={{ width: 100, whiteSpace: 'nowrap' }}>{new Date(exp.registered_at).toLocaleDateString('pt-BR')}</td>
                              <td className="p-4 text-right" style={{ width: 80 }}>
                                <div className="flex gap-1.5 justify-end">
                                  <button 
                                    onClick={() => handleStartEdit(exp)}
                                    className="p-1.5 text-slate-400 hover:text-blue hover:bg-white/5 rounded-lg transition-colors"
                                    title="Editar"
                                  >
                                    <Pencil size={14} />
                                  </button>
                                  <button 
                                    onClick={() => handleDeleteExpense(exp.id)}
                                    className="p-1.5 text-slate-400 hover:text-sf-red hover:bg-white/5 rounded-lg transition-colors"
                                    title="Excluir"
                                  >
                                    <Trash2 size={14} />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot className="bg-navy4/20 font-bold border-t border-white/5">
                        <tr>
                          <td className="p-4 text-slate-300 text-sm" colSpan={4}>Total de gastos</td>
                          <td className="p-4 text-right text-sm" style={{ width: 110, whiteSpace: 'nowrap', color: 'var(--sf-red)', fontWeight: 'bold' }}>
                            − {formatBRL(orderExpensesTotal)}
                          </td>
                          <td className="p-4" style={{ width: 100 }}></td>
                          <td className="p-4" style={{ width: 80 }}></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="card p-12 text-center text-slate-400 flex flex-col items-center gap-3">
              <AlertCircle className="text-slate-500" size={32} />
              <span>Nenhuma ordem de serviço selecionada. Filtre ou selecione para começar.</span>
            </div>
          )}
        </div>
      ) : (
              <div className="space-y-6">
          {/* Filters Bar */}
          <div className="card p-6 rounded-3xl grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 items-end">
            {/* 1. Período */}
            <div className="space-y-1.5">
              <label className="text-[11px] font-bold uppercase tracking-wider" style={{ color: 'var(--muted)' }}>
                PERÍODO
              </label>
              <select 
                className="input-field w-full h-11"
                value={filterPeriod}
                onChange={(e) => setFilterPeriod(e.target.value)}
              >
                <option value="30">Últimos 30 dias</option>
                <option value="90">Últimos 90 dias</option>
                <option value="180">Últimos 180 dias</option>
                <option value="365">Últimos 12 meses</option>
                <option value="ALL">Todo o período</option>
              </select>
            </div>

            {/* 2. Técnico */}
            <div className="space-y-1.5">
              <label className="text-[11px] font-bold uppercase tracking-wider" style={{ color: 'var(--muted)' }}>
                TÉCNICO
              </label>
              <select 
                className="input-field w-full h-11"
                value={filterTechnician}
                onChange={(e) => setFilterTechnician(e.target.value)}
              >
                <option value="ALL">Todos</option>
                {techniciansList.map(t => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>

            {/* 3. Status O.S. */}
            <div className="space-y-1.5">
              <label className="text-[11px] font-bold uppercase tracking-wider" style={{ color: 'var(--muted)' }}>
                STATUS DA O.S.
              </label>
              <select 
                className="input-field w-full h-11"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="ALL">Todos</option>
                <option value="IN_PROGRESS">Em andamento</option>
                <option value="COMPLETED">Concluída</option>
              </select>
            </div>

            {/* 4. Buscar Item */}
            <div className="space-y-1.5">
              <label className="text-[11px] font-bold uppercase tracking-wider" style={{ color: 'var(--muted)' }}>
                BUSCAR ITEM
              </label>
              <input 
                type="text"
                placeholder="Ex: Cola, Broca..."
                className="input-field w-full h-11"
                value={filterItemName}
                onChange={(e) => setFilterItemName(e.target.value)}
              />
            </div>
          </div>

          {/* 4 Metrics Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
            {/* Card 1 */}
            <div className="card p-5 rounded-3xl flex flex-col gap-1">
              <span className="text-[11px] font-bold uppercase tracking-wider" style={{ color: 'var(--muted)' }}>TOTAL EM GASTOS</span>
              <span className="text-xl font-bold mt-1" style={{ color: 'var(--sf-red)' }}>{formatBRL(generalExpensesTotal)}</span>
              <span className="text-[11px]" style={{ color: 'var(--muted)' }}>{getPeriodLabel()}</span>
            </div>

            {/* Card 2 */}
            <div className="card p-5 rounded-3xl flex flex-col gap-1">
              <span className="text-[11px] font-bold uppercase tracking-wider" style={{ color: 'var(--muted)' }}>O.S. COM GASTOS</span>
              <span className="text-xl font-bold mt-1 text-white">{numOsWithExpenses}</span>
              <span className="text-[11px]" style={{ color: 'var(--muted)' }}>de {orders.filter(o => o.type === 'ASSEMBLY').length} montagens</span>
            </div>

            {/* Card 3 */}
            <div className="card p-5 rounded-3xl flex flex-col gap-1">
              <span className="text-[11px] font-bold uppercase tracking-wider" style={{ color: 'var(--muted)' }}>GASTO MÉDIO POR O.S.</span>
              <span className="text-xl font-bold mt-1 text-white">{formatBRL(averageExpensePerOs)}</span>
              <span className="text-[11px]" style={{ color: 'var(--muted)' }}>Ticket médio</span>
            </div>

            {/* Card 4 */}
            <div className="card p-5 rounded-3xl flex flex-col gap-1">
              <span className="text-[11px] font-bold uppercase tracking-wider" style={{ color: 'var(--muted)' }}>ITEM MAIS COMPRADO</span>
              <span className="text-xl font-bold mt-1 text-white truncate" title={mostPurchasedItem}>{mostPurchasedItem}</span>
              <span className="text-[11px]" style={{ color: 'var(--muted)' }}>{mostPurchasedQty} registros</span>
            </div>
          </div>

          {/* Table "Resultado por O.S." */}
          <div className="card rounded-3xl overflow-hidden flex flex-col">
            <div className="p-6 border-b border-white/5 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div className="flex items-center gap-2">
                <List size={18} style={{ color: 'var(--sf-blue)' }} />
                <h3 className="font-bold text-[16px] text-slate-200">Resultado por O.S.</h3>
              </div>
              {osConsolidatedList.length > 0 && (
                <Button
                  variant="secondary"
                  onClick={exportCSVGeneralConsolidated}
                  icon={<Download size={18} />}
                >
                  Exportar
                </Button>
              )}
            </div>

            {loading ? (
              <div className="p-12 text-center text-slate-400">Carregando painel geral...</div>
            ) : osConsolidatedList.length === 0 ? (
              <div className="p-12 text-center text-slate-400 flex flex-col items-center gap-3">
                <AlertCircle className="text-slate-500" size={32} />
                <span>Nenhum custo consolidado encontrado com os filtros atuais.</span>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-navy4/30 border-b border-white/5">
                      <th className="p-4 text-[11px] font-bold uppercase tracking-wider text-slate-400">O.S.</th>
                      <th className="p-4 text-[11px] font-bold uppercase tracking-wider text-slate-400">CLIENTE</th>
                      <th className="p-4 text-[11px] font-bold uppercase tracking-wider text-slate-400">TÉCNICO</th>
                      <th className="p-4 text-[11px] font-bold uppercase tracking-wider text-slate-400">STATUS</th>
                      <th className="p-4 text-[11px] font-bold uppercase tracking-wider text-slate-400 text-right">VALOR O.S.</th>
                      <th className="p-4 text-[11px] font-bold uppercase tracking-wider text-slate-400 text-right">GASTOS</th>
                      <th className="p-4 text-[11px] font-bold uppercase tracking-wider text-slate-400 text-right">RESULTADO LÍQUIDO</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5 text-sm">
                    {osConsolidatedList.map((item) => {
                      const stStyle = getTableStatusStyle(item.status);
                      const badgeStyle = getOsBadgeStyle(item.status);
                      const net = item.orderValue - item.expensesTotal;

                      return (
                        <tr key={item.orderId} className="hover:bg-white/[0.01]">
                          <td className="p-4 font-mono font-bold">
                            <span 
                              style={{ background: badgeStyle.bg, color: badgeStyle.color }}
                              className="px-2.5 py-1 rounded-full text-xs font-semibold"
                            >
                              #{item.orderId.substring(0, 8)}
                            </span>
                          </td>
                          <td className="p-4 text-slate-100 font-semibold">{item.clientName}</td>
                          <td className="p-4 text-slate-400">{item.technicianName}</td>
                          <td className="p-4">
                            <span 
                              style={{ background: stStyle.bg, color: stStyle.color }}
                              className="px-2.5 py-1 rounded-full text-xs font-semibold"
                            >
                              {stStyle.label}
                            </span>
                          </td>
                          <td className="p-4 text-right text-slate-300 font-medium">{formatBRL(item.orderValue)}</td>
                          <td className="p-4 text-right text-sm font-medium" style={{ color: 'var(--sf-red)', whiteSpace: 'nowrap' }}>
                            {item.expensesTotal > 0 ? `− ${formatBRL(item.expensesTotal)}` : ''}
                          </td>
                          <td className="p-4 text-right font-bold" style={{ color: 'var(--sf-green)', whiteSpace: 'nowrap' }}>
                            {formatBRL(net)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot className="bg-navy4/20 font-bold border-t border-white/5 text-sm">
                    <tr>
                      <td className="p-4 text-slate-300" colSpan={4}>Total do período</td>
                      <td className="p-4 text-right text-slate-100 font-bold">
                        {formatBRL(generalBrutoTotal)}
                      </td>
                      <td className="p-4 text-right font-bold" style={{ color: 'var(--sf-red)' }}>
                        − {formatBRL(generalGastosTotal)}
                      </td>
                      <td className="p-4 text-right font-bold" style={{ color: 'var(--sf-green)' }}>
                        {formatBRL(generalLiquidoTotal)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>

          {/* Table "Itens mais comprados no período" */}
          <div className="card rounded-3xl overflow-hidden flex flex-col">
            <div className="p-6 border-b border-white/5 flex items-center gap-2">
              <TrendingUp size={18} style={{ color: 'var(--sf-blue)' }} />
              <h3 className="font-bold text-[16px] text-slate-200">Itens mais comprados no período</h3>
            </div>

            {loading ? (
              <div className="p-12 text-center text-slate-400">Carregando itens...</div>
            ) : itemRankingList.length === 0 ? (
              <div className="p-12 text-center text-slate-400 flex flex-col items-center gap-3">
                <AlertCircle className="text-slate-500" size={24} />
                <span>Sem dados para exibir.</span>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse text-sm">
                  <thead>
                    <tr className="bg-navy4/30 border-b border-white/5">
                      <th className="p-4 text-[11px] font-bold uppercase tracking-wider text-slate-400 text-center w-12">#</th>
                      <th className="p-4 text-[11px] font-bold uppercase tracking-wider text-slate-400">ITEM</th>
                      <th className="p-4 text-[11px] font-bold uppercase tracking-wider text-slate-400 text-center w-36">OCORRÊNCIAS</th>
                      <th className="p-4 text-[11px] font-bold uppercase tracking-wider text-slate-400 text-right w-44">TOTAL GASTO</th>
                      <th className="p-4 text-[11px] font-bold uppercase tracking-wider text-slate-400 text-right w-44">% DOS GASTOS</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {itemRankingList.map((item, index) => {
                      const percent = generalExpensesTotal > 0 ? (item.totalSpent / generalExpensesTotal) * 100 : 0;
                      return (
                        <tr key={item.name} className="hover:bg-white/[0.01]">
                          <td className="p-4 text-center text-slate-400 font-mono font-bold">{index + 1}</td>
                          <td className="p-4 font-semibold text-slate-200 truncate" title={item.name}>{item.name}</td>
                          <td className="p-4 text-center font-medium">{item.occurrences}</td>
                          <td className="p-4 text-right font-medium" style={{ color: 'var(--sf-red)' }}>{formatBRL(item.totalSpent)}</td>
                          <td className="p-4 text-right text-slate-400 text-xs font-semibold">{percent.toFixed(1).replace('.', ',')}%</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {confirmModal && (
        <ConfirmModal
          open={!!confirmModal}
          title={confirmModal.title}
          message={confirmModal.message}
          confirmLabel={confirmModal.confirmLabel}
          cancelLabel={confirmModal.cancelLabel}
          confirmStyle={confirmModal.confirmStyle}
          onConfirm={confirmModal.onConfirm}
          onCancel={confirmModal.onCancel}
        />
      )}
    </div>
  );
};
